import {
  ga4RawParams,
  getGoogleAnalyticsClientId,
  getGoogleAnalyticsSessionId,
  type AnalyticsProperties,
} from "@/lib/analytics-ga4";
import { getOrCreateBootstrapDistinctId } from "@/lib/analytics-identity";
import { getFirstTouchAttribution } from "@/lib/landing-cluster";
import { isBrowserTelemetrySuppressed } from "@/lib/sensitive-route-policy";
import { browserTrafficMarker, isAutomatedBrowser } from "@/lib/synthetic-traffic";
import {
  isBrowserThirdPartyTelemetrySuppressed,
  privacySafeTelemetryProperties,
  privacySafeTelemetryUrl,
} from "@/lib/telemetry-route-policy";
import { eventIdFromProperties, type BeaconEvent } from "@/lib/beacon-types";
import {
  acknowledgeDurableEvents,
  pendingDurableEvents,
  persistDurableEvent,
  type DurableBeaconEvent,
} from "@/lib/analytics-durable-outbox";

interface EventRoutingPolicy {
  readonly serverCanonical: boolean;
  readonly forwardToGa4: boolean;
  readonly immediateFlush: boolean;
  readonly durableDelivery?: boolean;
}

const SERVER_BATCHED: EventRoutingPolicy = {
  serverCanonical: true,
  forwardToGa4: true,
  immediateFlush: false,
};
const SERVER_IMMEDIATE: EventRoutingPolicy = {
  serverCanonical: true,
  forwardToGa4: true,
  immediateFlush: true,
};
const BIGQUERY_ONLY_IMMEDIATE: EventRoutingPolicy = {
  serverCanonical: true,
  forwardToGa4: false,
  immediateFlush: true,
};
const OWNED_IMMEDIATE: EventRoutingPolicy = {
  serverCanonical: false,
  forwardToGa4: false,
  immediateFlush: true,
};
const OWNED_DURABLE_IMMEDIATE: EventRoutingPolicy = {
  serverCanonical: false,
  forwardToGa4: false,
  immediateFlush: true,
  durableDelivery: true,
};
const OWNED_BATCHED: EventRoutingPolicy = {
  serverCanonical: false,
  forwardToGa4: false,
  immediateFlush: false,
};

const SERVER_DURABLE_BATCHED: EventRoutingPolicy = {
  serverCanonical: true,
  forwardToGa4: true,
  immediateFlush: false,
  durableDelivery: true,
};
const OWNED_DURABLE_BATCHED: EventRoutingPolicy = {
  serverCanonical: false,
  forwardToGa4: false,
  immediateFlush: false,
  durableDelivery: true,
};

// One code-local routing policy owns the server-canonical, GA4-forwarding, and
// flush-timing decisions. Do not project these decisions into funnel metadata:
// the transport owns them, while the event schema owns names and properties.
const EVENT_ROUTING_POLICY: Readonly<Record<string, EventRoutingPolicy>> = {
  agent_revenue_offer_clicked: SERVER_BATCHED,
  agent_revenue_offer_dismissed: SERVER_BATCHED,
  // Preserve the impression alongside the durable experiment denominator.
  // Keep batching; retry uses the same event id and original browser time.
  agent_revenue_offer_shown: SERVER_DURABLE_BATCHED,
  offer_card_seen: OWNED_DURABLE_BATCHED,
  agentic_cart_bundle_shown: SERVER_BATCHED,
  agentic_cart_followup_sent: SERVER_BATCHED,
  agentic_cart_opened: SERVER_BATCHED,
  agentic_cart_order_created: SERVER_IMMEDIATE,
  agentic_cart_paid: SERVER_IMMEDIATE,
  agentic_cart_shopify_mirrored: SERVER_BATCHED,
  ai_chat_reply_received: SERVER_BATCHED,
  ai_astrologer_opened: SERVER_BATCHED,
  astrologer_page_viewed: SERVER_BATCHED,
  calculator_used: SERVER_BATCHED,
  chat_limit_reached: SERVER_IMMEDIATE,
  chat_message_sent: SERVER_BATCHED,
  chat_started: SERVER_BATCHED,
  chat_tool_invoked: SERVER_BATCHED,
  contact_captured: SERVER_IMMEDIATE,
  first_meaningful_question: SERVER_BATCHED,
  free_tool_completed: SERVER_BATCHED,
  free_tool_started: SERVER_BATCHED,
  internal_link_clicked: SERVER_BATCHED,
  kundali_form_started: SERVER_BATCHED,
  kundali_form_step: SERVER_BATCHED,
  kundali_form_submitted: SERVER_BATCHED,
  kundali_form_submit_failed: SERVER_BATCHED,
  kundali_generated: SERVER_BATCHED,
  kundali_result_viewed: SERVER_BATCHED,
  matching_completed: SERVER_BATCHED,
  outbound_link_clicked: SERVER_BATCHED,
  page_engaged: SERVER_BATCHED,
  paid_cta_clicked: SERVER_IMMEDIATE,
  paid_report_nudge_shown: SERVER_BATCHED,
  paywall_shown: SERVER_BATCHED,
  premium_unlock_shown: SERVER_BATCHED,
  report_checkout_abandoned: SERVER_IMMEDIATE,
  report_checkout_blocked: SERVER_IMMEDIATE,
  report_checkout_form_shown: SERVER_BATCHED,
  report_checkout_recovery_clicked: SERVER_IMMEDIATE,
  report_checkout_recovered: SERVER_IMMEDIATE,
  report_checkout_started: SERVER_IMMEDIATE,
  report_offer_cta_tapped: SERVER_IMMEDIATE,
  report_delivered: SERVER_IMMEDIATE,
  // Server-canonical events whose canonical home is the first-party BigQuery
  // sink (events_raw) — NOT GA4. They are funnel/diagnostic mirrors of the
  // real purchase that would otherwise land in GA4 as purchase-looking custom
  // events and pollute the ecommerce picture (the 2026-06-13 cleanup). GA4
  // ecommerce is the reserved `purchase` event only, fired server-side from
  // /payment/verify. These still flow to events_raw for funnel analysis; they
  // just stop being forwarded to GA4.
  report_purchased: BIGQUERY_ONLY_IMMEDIATE,
  report_remedy_kit_upsell_clicked: SERVER_BATCHED,
  report_remedy_kit_upsell_shown: SERVER_BATCHED,
  report_upi_payment_timeout: SERVER_BATCHED,
  chat_upi_payment_timeout: SERVER_BATCHED,
  revenue_completed: BIGQUERY_ONLY_IMMEDIATE,
  razorpay_checkout_closed: SERVER_IMMEDIATE,
  razorpay_checkout_opened: SERVER_IMMEDIATE,
  scroll_depth: SERVER_BATCHED,
  seo_agent_bridge_clicked: SERVER_BATCHED,
  seo_agent_bridge_viewed: SERVER_BATCHED,
  seo_intent_captured: SERVER_BATCHED,
  seo_page_viewed: SERVER_BATCHED,
  shopify_cart_started: SERVER_IMMEDIATE,
  shopify_outbound_click: SERVER_IMMEDIATE,
  shopify_product_clicked: SERVER_IMMEDIATE,
  // site_page_viewed removed 2026-05-24 — duplicate of $pageview;
  // ~3.4K events/day toward the 1M/mo PostHog free-tier cap.
  // A/B exposure MUST deliver reliably or the split skews (Sample Ratio Mismatch):
  // an arm with more friction (e.g. the phone-gate "required"/"optional" arms) makes
  // users bounce BEFORE a 30s-batched exposure flushes, so its exposures are
  // under-logged relative to a smoother arm → the split looks imbalanced and the CVR
  // verdict becomes untrustworthy (phone_gate_2026_07 SRM p=0.002, 2026-07-09).
  // Exposure is ≤1/session/experiment (deduped), so immediate flush is cheap.
  experiment_exposed: OWNED_DURABLE_IMMEDIATE,
  // Push activation ends in a permission dialog / browser-service boundary;
  // the seeker may leave immediately after it fails. Flush the one terminal
  // stage receipt now so the post-permission success denominator is durable.
  daily_reading_push_activation_outcome: OWNED_IMMEDIATE,
  // Activation failures must survive the same short-bounce moment as the
  // permission prompt. The event is privacy-minimized + owned-only and is the
  // single terminal diagnostic for each activation attempt.
  daily_reading_push_failed: OWNED_IMMEDIATE,
  // A JS error often precedes a crash/navigation, so flush it before the page
  // can unmount. The js_error emitter caps at ≤5 beacons/session (de-duped), so
  // immediate flush is at most 5 POSTs — well within the edge-beacon budget.
  js_error: OWNED_IMMEDIATE,
  // The native GA-only boundary is the critical-document vendor collector. Its
  // first-party gateway failure must be visible even when the user bounces
  // before the normal batch timer, and must never be forwarded back to GA4.
  ga4_tag_bootstrap_failed: OWNED_IMMEDIATE,
  // First-party route-entry truth must survive short bounces. It is owned-only
  // (never GA4-forwarded), so an immediate flush adds no Google double-write.
  page_view: OWNED_IMMEDIATE,
};

function eventRoutingPolicy(name: string): EventRoutingPolicy {
  return EVENT_ROUTING_POLICY[name] ?? OWNED_BATCHED;
}

/**
 * True for events whose canonical GA4 writer is the backend (either the
 * edge beacon for browser-originated events, or the Razorpay verify
 * webhook for paid commerce). Clients call this so `trackEvent` can
 * skip its gtag/GTM fanout for these — otherwise GA4 receives 2-3× per fire.
 */
export function isServerCanonicalEvent(name: string): boolean {
  return eventRoutingPolicy(name).serverCanonical;
}

type QueuedEvent = DurableBeaconEvent & {
  page_location: string;
  page_title: string;
  page_referrer: string;
  engagement_time_msec: number;
  durable_delivery?: boolean;
};

// Native gtag owns GA engagement. Supplemental Measurement Protocol events
// carry only the 1ms transport minimum so behavioral events cannot cumulatively
// re-earn the same visible time and inflate userEngagementDuration.
function currentEngagementMs(): number {
  return 1;
}

// Batching parameters — chosen to reduce beacon request count without sampling.
// pagehide still flushes short sessions before the browser leaves the page, and
// conversion-critical events below bypass the long timer.
const BATCH_FLUSH_MS = 30_000;
const BATCH_MAX_EVENTS = 50;

let queue: QueuedEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let pageHideHookInstalled = false;
const durableEventsInFlight = new Set<string>();

const BEACON_PATH = "/_beacon/";
const PAGE_VIEW_BEACON_PATH = "/_beacon/page-view/";

export function beaconUrl(eventName?: string): string {
  // Same-origin in prod. Keep the trailing slash: page paths are slash-canonical,
  // and sendBeacon POST bodies must never cross a 301/308 redirect.
  // Page views use a dedicated path so Cloudflare HTTP analytics remains an
  // independent, zero-BigQuery page-load counter. Every other event stays on
  // the general beacon path.
  const path = eventName === "page_view" ? PAGE_VIEW_BEACON_PATH : BEACON_PATH;
  if (typeof window === "undefined") return path;
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1" || host.endsWith(".local")) {
    // Dev: keep the legacy backend route (still wired).
    return path;
  }
  return path;
}

function ensurePageHideHook() {
  if (pageHideHookInstalled || typeof window === "undefined") return;
  pageHideHookInstalled = true;
  // `pagehide` is the modern replacement for `unload`; fires reliably on
  // mobile tab-close + back-nav where `beforeunload` doesn't.
  window.addEventListener(
    "pagehide",
    () => {
      flushQueue(true);
    },
    { capture: true }
  );
  // Best-effort fallback for tab visibility changes (e.g. user switches
  // apps mid-session — we want events from the previous batch to land).
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      flushQueue(true);
    }
  });
}

function scheduleFlush() {
  if (flushTimer !== null) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushQueue(false);
  }, BATCH_FLUSH_MS);
}

function flushQueue(useBeacon: boolean) {
  if (queue.length === 0) return;
  // Resolve Google's cookie identity as late as possible. The event may have
  // been queued before deferred GTM/gtag created its cookies; binding at
  // enqueue time split one browser between the owned UUID and a later GA ID.
  const events = attachMeasurementIdentity(queue);
  queue = [];
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }

  // Keep page views alone on the dedicated transport even if an immediate
  // route-entry flush happens to drain previously queued engagement events.
  // This makes /_beacon/page-view/ an exact request-level page-view counter.
  const pageViews = events.filter((event) => event.name === "page_view");
  const otherEvents = events.filter((event) => event.name !== "page_view");
  sendEventBatch(pageViews, useBeacon, beaconUrl("page_view"));
  sendEventBatch(otherEvents, useBeacon, beaconUrl());
}

function wireEvent(event: QueuedEvent): BeaconEvent {
  const { durable_delivery: _durableDelivery, ...wire } = event;
  return wire;
}

function retryableResponse(response: Response): boolean {
  return response.status === 408 || response.status === 429 || response.status >= 500;
}

function finishDurableAttempt(eventIds: Set<string>, acknowledged: boolean): void {
  if (acknowledged) acknowledgeDurableEvents(eventIds);
  for (const eventId of eventIds) durableEventsInFlight.delete(eventId);
}

function sendEventBatch(events: QueuedEvent[], useBeacon: boolean, url: string) {
  if (events.length === 0) return;
  const payload = JSON.stringify({ events: events.map(wireEvent) });
  const durableIds = new Set(
    events.filter((event) => event.durable_delivery).map((event) => event.event_id)
  );
  for (const eventId of durableIds) durableEventsInFlight.add(eventId);
  try {
    if (useBeacon && navigator.sendBeacon) {
      const blob = new Blob([payload], { type: "application/json" });
      if (navigator.sendBeacon(url, blob)) {
        finishDurableAttempt(durableIds, false);
        return;
      }
    }
    void fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
      keepalive: true,
    })
      .then((response) => finishDurableAttempt(durableIds, !retryableResponse(response)))
      .catch(() => finishDurableAttempt(durableIds, false));
  } catch {
    // Browser analytics must never block the product.
    finishDurableAttempt(durableIds, false);
  }
}

function replayDurableOutbox(): void {
  const queuedIds = new Set(queue.map((event) => event.event_id));
  const pending = pendingDurableEvents()
    .filter((event) => !durableEventsInFlight.has(event.event_id) && !queuedIds.has(event.event_id))
    .map((event) => ({ ...event, durable_delivery: true }) as QueuedEvent);
  if (pending.length > 0) sendEventBatch(pending, false, beaconUrl());
}

function attachMeasurementIdentity(events: QueuedEvent[]): QueuedEvent[] {
  const measurementClientId = getGoogleAnalyticsClientId();
  const measurementSessionId = getGoogleAnalyticsSessionId();
  return events.map((event) => {
    if (event.forward_to_ga4 === false || !measurementClientId) return event;
    return {
      ...event,
      measurement_client_id: measurementClientId,
      ...(measurementSessionId ? { measurement_session_id: measurementSessionId } : {}),
    };
  });
}

function ownedEventClientId(): string | null {
  if (typeof window === "undefined") return null;
  if (isBrowserTelemetrySuppressed() || isAutomatedBrowser()) return null;
  const ownedClientId = getOrCreateBootstrapDistinctId();
  return ownedClientId ?? null;
}

function enqueueOwnedEvent(
  name: string,
  properties: AnalyticsProperties | undefined,
  options: { forwardToGa4: boolean; immediateFlush: boolean }
) {
  const ownedClientId = ownedEventClientId();
  if (!ownedClientId) return;

  replayDurableOutbox();
  const safeProperties = privacySafeTelemetryProperties(properties ?? {});

  const queuedEvent: QueuedEvent = {
    name,
    event_id: eventIdFromProperties(properties),
    client_id: ownedClientId,
    forward_to_ga4: options.forwardToGa4,
    page_location: privacySafeTelemetryUrl(window.location.href),
    page_title: typeof document !== "undefined" ? document.title : "",
    page_referrer:
      typeof document !== "undefined" ? privacySafeTelemetryUrl(document.referrer) : "",
    engagement_time_msec: currentEngagementMs(),
    params: ga4RawParams(name, {
      ...safeProperties,
      // The same governed first-touch contract feeds behavioral and payment
      // legs, so channel/path funnels join without reconstructing acquisition
      // from a later page. Raw click IDs and referrer queries are never stored.
      ...(getFirstTouchAttribution() ?? {}),
      // Applied centrally and after caller properties so direct owned emitters
      // (including js_error) cannot let a browser monitor enter human marts.
      ...browserTrafficMarker(),
      client_event_ts: new Date().toISOString(),
      // Owned browser id — the SAME id recorded in Razorpay order notes, so
      // events_raw reconciles to server-canonical orders (stg_events_raw keys
      // on params.distinct_id, falling back to the GA client_id).
      distinct_id: ownedClientId,
    }),
    durable_delivery: eventRoutingPolicy(name).durableDelivery,
  };
  if (queuedEvent.durable_delivery) persistDurableEvent(queuedEvent);
  queue.push(queuedEvent);
  ensurePageHideHook();

  if (options.immediateFlush) {
    flushQueue(false);
  } else if (queue.length >= BATCH_MAX_EVENTS) {
    flushQueue(false);
  } else {
    scheduleFlush();
  }
}

export function sendServerAnalyticsEvent(name: string, properties?: AnalyticsProperties) {
  const policy = eventRoutingPolicy(name);
  if (!policy.serverCanonical) return;
  enqueueOwnedEvent(name, properties, {
    forwardToGa4: policy.forwardToGa4 && !isBrowserThirdPartyTelemetrySuppressed(),
    immediateFlush: policy.immediateFlush,
  });
}

export function sendOwnedAnalyticsEvent(name: string, properties?: AnalyticsProperties) {
  enqueueOwnedEvent(name, properties, {
    forwardToGa4: false,
    immediateFlush: eventRoutingPolicy(name).immediateFlush,
  });
}
